import { hasClosedInZone } from "@/utils/storeClock";

/**
 * storeStatus.js
 *
 * Shared helper for mapping a store/campaign record onto the four
 * design-system status pills used on the distributor Stores list:
 * live / draft / pending / closed, plus archived (the Archived view).
 *
 * "Pending" is omitted here because the current backend model has no
 * distinct pending state; rendering an option that can never select
 * rows would be a dead pill. See StoreList.vue history for context.
 *
 * Used by:
 *   - StoreList.vue (status filter + lede)
 *   - StoreListTable.vue (StatusChip in the Status column)
 *   - StoreEditShell.vue (the editor's status pill, so the two agree)
 *
 * Distributor design fidelity, handoff_2026-05-09/2026-05-29-distributor-account.
 *
 * Pre-fix returned "live" any time active=true, which contradicted the
 * Schedule column when end_date had already passed -- the row rendered
 * a LIVE pill alongside an "Ended <date>" chip. is_permanent_store
 * wins (permanent stores have no end_date semantics).
 *
 * The close is read on the DISTRIBUTOR's clock (the row's
 * `schedule_timezone`), the same rule as the server's Store.has_closed.
 * Parsing the naive end_date ("2026-10-07 20:00:00") as an instant read
 * it in the VIEWER's zone: a Pacific viewer saw an Eastern store still
 * open 3h past its close.
 *
 * @param {object} item - the store row (campaign object from the API).
 * @param {Date} [now] - injectable clock for tests. Defaults to new
 *                       Date() in production callers.
 */
export function deriveStoreStatus(item, now = new Date()) {
    if (!item) return "draft";
    // ARCHIVED wins over every other flag: the store is hidden from
    // shoppers and from the default list, and only the Archived view shows
    // it (spec docs/planning/store-counting-rules-2026-10-15.md 4.3 PR 1).
    if (item.archived_at) return "archived";
    if (item.complete || item.is_manually_closed) return "closed";
    if (item.active) {
        // Permanent stores never end. Even if a stale end_date is
        // present on a row that was switched from pop-up to permanent
        // without nulling the field, the permanent flag is
        // authoritative.
        if (item.is_permanent_store) return "live";
        if (hasClosedInZone(item.end_date, item.schedule_timezone, now)) {
            return "closed";
        }
        return "live";
    }
    return "draft";
}
